import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Loader2, ArrowLeft, TrendingUp, Scale, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Terracotta + this blue survive colour-blindness simulation (protan/deutan/tritan).
// The sage green of the design system does NOT — it collapses to grey next to the
// terracotta — so it is used for accents here, never to distinguish two data series.
const C_MAIN = '#D96C48';
const C_ALT = '#0F86B8';
const C_GRID = '#E6E2DC';
const C_STONE = '#787570';
const C_SAGE = '#6B8E6B';

const L = {
  es: {
    title: 'Evolución', back: 'Volver a Entrenamiento',
    premiumOnly: 'Solo para usuarios Premium.', upgrade: 'Mejorar a Premium',
    allPlans: 'Todos los planes', plan: 'Plan',
    adherence: 'Adherencia', ofSessions: '{done} de {total} sesiones',
    volume: 'Volumen total', volumeD: 'kilos movidos, solo series marcadas',
    bodyWeight: 'Peso corporal', weightD: 'desde {first} kg', weightNone: 'aún sin registrar',
    completeWeeks: 'Semanas completas', completeWeeksD: 'seguidas, con todas sus sesiones hechas',
    weightTitle: 'Peso corporal',
    weightWhy: 'Cada vez que guardas tu peso en el perfil queda un punto aquí. Antes solo se guardaba el último valor y se sobrescribía, así que esta gráfica empieza hoy y se llena a partir de ahora.',
    weightEmpty: 'Todavía no hay ningún peso registrado. Guarda el primero y la gráfica aparecerá aquí.',
    weightOne: 'Solo hay un registro. Con el segundo ya podrás ver la línea.',
    logWeight: 'Registrar peso de hoy', save: 'Guardar',
    logHint: 'También se guarda solo cada vez que cambies el peso en tu perfil.',
    weightSaved: 'Peso guardado', weightBad: 'Introduce un peso válido',
    strengthTitle: 'Fuerza',
    strengthWhy: '1RM estimado con la fórmula de Epley sobre tu mejor serie de cada sesión. Cada punto es un día que entrenaste de verdad.',
    strengthEmpty: 'Aún no hay series marcadas como hechas. En cuanto completes un día con pesos y repeticiones, verás aquí tu progresión.',
    exercise: 'Ejercicio', estRM: '1RM estimado', deloadWeek: 'Semana de descarga',
    records: 'Tus récords', exCol: 'Ejercicio', bestSet: 'Mejor serie', rmCol: '1RM est.', whenCol: 'Cuándo',
    bwTag: 'peso corporal',
    unilateralNote: 'En unilaterales (mancuernas alternando) el 1RM es por brazo: peso de una mancuerna y repeticiones de un brazo.',
    bodyweightNote: 'En ejercicios de peso corporal se suma la parte de tu cuerpo que levantas, usando tu peso de ese día.',
    volumeTitle: 'Volumen por semana',
    volumeWhy: 'Kilos totales movidos (repeticiones × peso). La semana de descarga debe bajar: si no baja, no estás descargando.',
    volumeEmpty: 'Aún no hay volumen registrado.',
    deload: 'descarga', sessionsShort: 'sesiones',
    cardioTitle: 'Cardio',
    cardioWhy: 'Correr y nadar van en gráficas separadas a propósito: son metros los dos, pero de escalas muy distintas, y juntarlos aplastaría la de natación.',
    cardioEmpty: 'Aún no hay distancias registradas en sesiones completadas.',
    metersPerSession: 'metros por sesión',
    pacePerSession: 'ritmo — más abajo es más rápido', paceRun: 'min/km', paceSwim: 'min/100m',
    onlyDone: 'Solo cuentan las series y sesiones que marcaste como hechas.',
    kg: 'kg', reps: 'reps', t: 't',
    types: { running: 'Carrera', swimming: 'Natación', cycling: 'Ciclismo', otro: 'Otro' },
    deleteEntry: 'Borrar este registro de peso?',
  },
  en: {
    title: 'Progress', back: 'Back to Training',
    premiumOnly: 'Premium users only.', upgrade: 'Upgrade to Premium',
    allPlans: 'All plans', plan: 'Plan',
    adherence: 'Adherence', ofSessions: '{done} of {total} sessions',
    volume: 'Total volume', volumeD: 'kilos moved, only ticked sets',
    bodyWeight: 'Body weight', weightD: 'from {first} kg', weightNone: 'not logged yet',
    completeWeeks: 'Complete weeks', completeWeeksD: 'in a row, with every session done',
    weightTitle: 'Body weight',
    weightWhy: 'Every time you save your weight in your profile it becomes a point here. Before, only the latest value was kept and overwritten, so this chart starts today and fills up from now on.',
    weightEmpty: 'No weight logged yet. Save the first one and the chart will show up here.',
    weightOne: 'Only one entry so far. With the second one you will see the line.',
    logWeight: "Log today's weight", save: 'Save',
    logHint: 'It is also saved automatically whenever you change your weight in your profile.',
    weightSaved: 'Weight saved', weightBad: 'Enter a valid weight',
    strengthTitle: 'Strength',
    strengthWhy: 'Estimated 1RM using the Epley formula on your best set of each session. Every point is a day you actually trained.',
    strengthEmpty: 'No sets ticked as done yet. As soon as you complete a day with weights and reps, your progression shows up here.',
    exercise: 'Exercise', estRM: 'Estimated 1RM', deloadWeek: 'Deload week',
    records: 'Your records', exCol: 'Exercise', bestSet: 'Best set', rmCol: 'Est. 1RM', whenCol: 'When',
    bwTag: 'bodyweight',
    unilateralNote: 'For unilateral work (alternating dumbbells) the 1RM is per arm: the weight of one dumbbell and the reps of one arm.',
    bodyweightNote: 'For bodyweight exercises the share of your body you lift is added in, using your weight on that day.',
    volumeTitle: 'Volume per week',
    volumeWhy: 'Total kilos moved (reps × weight). The deload week should go down: if it does not, you are not deloading.',
    volumeEmpty: 'No volume logged yet.',
    deload: 'deload', sessionsShort: 'sessions',
    cardioTitle: 'Cardio',
    cardioWhy: 'Running and swimming get separate charts on purpose: both are metres, but on very different scales, and merging them would flatten swimming.',
    cardioEmpty: 'No distances logged in completed sessions yet.',
    metersPerSession: 'metres per session',
    pacePerSession: 'pace — lower is faster', paceRun: 'min/km', paceSwim: 'min/100m',
    onlyDone: 'Only the sets and sessions you ticked as done are counted.',
    kg: 'kg', reps: 'reps', t: 't',
    types: { running: 'Running', swimming: 'Swimming', cycling: 'Cycling', otro: 'Other' },
    deleteEntry: 'Delete this weight entry?',
  },
};

const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2026-07-06" -> "6 jul". Parsed by hand: new Date("YYYY-MM-DD") is UTC and would
// shift the day backwards for anyone west of Greenwich.
const shortDate = (iso, lang) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  const mm = (lang === 'en' ? MONTHS_EN : MONTHS_ES)[m - 1] || '';
  return lang === 'en' ? `${mm} ${d}` : `${d} ${mm}`;
};

const nf = (v, dec = 0, loc = 'es-ES') => (v == null || Number.isNaN(v) ? '—' : Number(v).toLocaleString(loc, { minimumFractionDigits: dec, maximumFractionDigits: dec }));

// Round axis ticks (Heckbert's "nice numbers"). Letting the chart pick its own domain
// produces ticks like 78,4 / 80,4 / 82,4 — technically right, unreadable in practice.
const niceNum = (range, round) => {
  const exp = Math.floor(Math.log10(range));
  const f = range / Math.pow(10, exp);
  const nf_ = round
    ? (f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10)
    : (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10);
  return nf_ * Math.pow(10, exp);
};

const niceScale = (values, maxTicks = 5) => {
  const vals = values.filter(v => typeof v === 'number' && !Number.isNaN(v));
  if (!vals.length) return { domain: [0, 1], ticks: [0, 1], step: 1 };
  let min = Math.min(...vals), max = Math.max(...vals);
  if (min === max) { min -= 1; max += 1; }        // a single point still needs an axis
  const step = niceNum(niceNum(max - min, false) / (maxTicks - 1), true);
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = lo; v <= hi + step * 0.5; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
  return { domain: [lo, hi], ticks, step };
};

function Tile({ label, value, detail }) {
  return (
    <Card className="bg-white rounded-2xl shadow-soft p-4">
      <p className="text-xs uppercase tracking-wide text-stone font-inter">{label}</p>
      <p className="font-manrope font-extrabold text-2xl text-charcoal mt-1">{value}</p>
      <p className="text-xs text-stone font-inter mt-1 leading-snug">{detail}</p>
    </Card>
  );
}

// mm:ss from a seconds value — used for cardio pace axes and tooltips.
const fmtPace = (sec) => {
  if (sec == null || Number.isNaN(sec)) return '—';
  const s = Math.round(Number(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

function ChartTip({ active, payload, label, unit, lang, extra, isPace }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  const loc = lang === 'en' ? 'en-US' : 'es-ES';
  return (
    <div className="bg-white border border-border rounded-xl shadow-soft px-3 py-2 text-xs font-inter">
      <p className="text-stone">{label}</p>
      <p className="font-bold text-charcoal">
        {isPace ? `${fmtPace(p.value)} ${unit}` : `${nf(p.value, unit === 'kg' ? 1 : 0, loc)} ${unit}`}
      </p>
      {extra && extra(p.payload)}
    </div>
  );
}

// Unit lives in a caption above the chart, not glued to every tick: repeating "kg" five
// times down the axis is noise, and it forces the label to wrap onto two lines.
function AxisUnit({ children }) {
  return <p className="text-[11px] text-stone font-inter mb-1 ml-1">{children}</p>;
}

function SectionHead({ title, children }) {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
      <h2 className="font-manrope font-bold text-lg text-charcoal">{title}</h2>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-stone font-inter italic py-8 text-center">{children}</p>;
}

export default function EvolutionPage() {
  const { language } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = L[language] || L.es;
  const isPremium = user?.plan === 'premium';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [planId, setPlanId] = useState('all');
  const [exIdx, setExIdx] = useState(0);
  const [wIn, setWIn] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    load(planId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium, planId]);

  const load = async (pid) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/training/evolution?plan_id=${encodeURIComponent(pid)}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setExIdx(0);
      }
    } catch (_) { /* leave the previous view up rather than blanking the page */ }
    setLoading(false);
  };

  const saveWeight = async () => {
    const v = parseFloat(String(wIn).replace(',', '.'));
    if (!v || v < 20 || v > 400) { toast.error(t.weightBad); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/training/weight-history`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weight_kg: v }),
      });
      if (res.ok) { setWIn(''); toast.success(t.weightSaved); await load(planId); }
      else toast.error(t.weightBad);
    } catch (_) { toast.error(t.weightBad); }
    setSaving(false);
  };

  const delWeight = async (id) => {
    if (!window.confirm(t.deleteEntry)) return;
    try {
      const res = await fetch(`${API}/training/weight-history/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) await load(planId);
    } catch (_) { /* nothing destructive happened; the list just stays as it was */ }
  };

  const loc = language === 'en' ? 'en-US' : 'es-ES';
  const fmt = (v, dec = 0) => nf(v, dec, loc);

  const s = data?.summary;
  const weight = data?.weight || [];
  const strength = data?.strength || [];
  const weeks = data?.weeks || [];
  const cardio = data?.cardio || {};

  const weightData = useMemo(
    () => weight.map(w => ({ ...w, x: shortDate(w.date, language) })),
    [weight, language]
  );
  const curEx = strength[exIdx];
  const rmData = useMemo(
    () => (curEx?.points || []).map(p => ({ ...p, x: shortDate(p.date, language) })),
    [curEx, language]
  );
  const volData = useMemo(
    () => weeks.map((w, i) => ({ ...w, x: `S${(w.week_index ?? i) + 1}` })),
    [weeks]
  );
  const cardioTypes = useMemo(
    () => Object.keys(cardio).filter(k => (cardio[k] || []).length > 0),
    [cardio]
  );

  const wScale = useMemo(() => niceScale(weightData.map(w => w.weight_kg)), [weightData]);
  const rmScale = useMemo(() => niceScale(rmData.map(p => p.one_rm)), [rmData]);
  // Volume bars are read against zero, so the axis must include it or the bars lie.
  const volScale = useMemo(() => niceScale([0, ...volData.map(v => v.volume_kg)]), [volData]);

  if (!isPremium) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <h1 className="font-manrope font-bold text-2xl text-charcoal mb-2">{t.title}</h1>
          <p className="text-stone font-inter mb-6">{t.premiumOnly}</p>
          <Button onClick={() => navigate('/pricing')} className="rounded-full bg-primary">{t.upgrade}</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/training')} className="flex items-center gap-2 text-stone hover:text-primary font-inter text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </button>

        <div className="flex items-end justify-between gap-4 flex-wrap mb-1">
          <h1 className="font-manrope font-bold text-3xl text-charcoal flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-primary" /> {t.title}
          </h1>
          {(data?.plans || []).length > 0 && (
            <select
              value={planId} onChange={(e) => setPlanId(e.target.value)} aria-label={t.plan}
              className="h-11 rounded-xl border border-border bg-white px-3 text-sm font-inter text-charcoal"
              data-testid="plan-select"
            >
              <option value="all">{t.allPlans}</option>
              {(data.plans || []).map(p => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
            </select>
          )}
        </div>
        <p className="text-stone font-inter text-sm mb-6">{t.onlyDone}</p>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-5">
            {/* ---------- SUMMARY ---------- */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile
                label={t.adherence}
                value={s?.adherence != null ? `${s.adherence}%` : '—'}
                detail={t.ofSessions.replace('{done}', s?.sessions_done ?? 0).replace('{total}', s?.sessions_total ?? 0)}
              />
              <Tile
                label={t.volume}
                value={s?.volume_kg ? `${fmt(s.volume_kg / 1000, 1)} ${t.t}` : '—'}
                detail={t.volumeD}
              />
              <Tile
                label={t.bodyWeight}
                value={s?.weight_current != null ? `${fmt(s.weight_current, 1)} ${t.kg}` : '—'}
                detail={s?.weight_first != null && s?.weight_points > 1
                  ? t.weightD.replace('{first}', fmt(s.weight_first, 1))
                  : t.weightNone}
              />
              <Tile label={t.completeWeeks} value={s?.complete_weeks ?? 0} detail={t.completeWeeksD} />
            </div>

            {/* ---------- BODY WEIGHT ---------- */}
            <Card className="bg-white rounded-3xl shadow-soft p-5 md:p-6">
              <SectionHead title={t.weightTitle} />
              <p className="text-sm text-stone font-inter mb-4">{t.weightWhy}</p>

              {weightData.length === 0 ? (
                <Empty>{t.weightEmpty}</Empty>
              ) : (
                <>
                  <AxisUnit>{t.kg}</AxisUnit>
                  <div className="h-56 -ml-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={weightData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                        <CartesianGrid stroke={C_GRID} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="x" tick={{ fontSize: 11, fill: C_STONE }} tickLine={false} axisLine={{ stroke: C_GRID }} />
                        <YAxis
                          tick={{ fontSize: 11, fill: C_STONE }} tickLine={false} axisLine={false}
                          domain={wScale.domain} ticks={wScale.ticks} width={42}
                          tickFormatter={(v) => fmt(v, wScale.step < 1 ? 1 : 0)}
                        />
                        <Tooltip content={<ChartTip unit="kg" lang={language} />} />
                        <Line
                          type="monotone" dataKey="weight_kg" stroke={C_MAIN} strokeWidth={2.5}
                          dot={{ r: 3, fill: C_MAIN }} activeDot={{ r: 5 }} isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  {weightData.length === 1 && <p className="text-xs text-stone font-inter italic text-center">{t.weightOne}</p>}
                </>
              )}

              <div className="flex items-end gap-2 flex-wrap mt-4 pt-4 border-t border-border">
                <div>
                  <label htmlFor="w-in" className="block text-xs text-stone font-inter mb-1">{t.logWeight}</label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="w-in" type="number" step="0.1" inputMode="decimal" placeholder="79,4"
                      value={wIn} onChange={(e) => setWIn(e.target.value)}
                      className="h-11 w-28 rounded-xl" data-testid="weight-input"
                    />
                    <span className="text-sm text-stone">{t.kg}</span>
                    <Button onClick={saveWeight} disabled={saving} className="h-11 rounded-full bg-primary" data-testid="weight-save">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4 mr-1" />}
                      {t.save}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-stone font-inter pb-3">{t.logHint}</p>
              </div>

              {weightData.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {[...weightData].reverse().slice(0, 6).map(w => (
                    <span key={w.entry_id || w.date} className="inline-flex items-center gap-2 text-xs font-inter bg-cream border border-border rounded-full pl-3 min-h-[44px]">
                      <b className="text-charcoal">{fmt(w.weight_kg, 1)}</b>
                      <span className="text-stone">{w.x}</span>
                      {/* 44px hit area: this is the one destructive control on the page and it
                          has to be missable-proof on a phone, not a 12px icon. */}
                      <button
                        onClick={() => delWeight(w.entry_id)} aria-label={`${t.deleteEntry} ${w.weight_kg} kg ${w.x}`}
                        className="w-11 h-11 flex items-center justify-center text-stone hover:text-primary rounded-full shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </Card>

            {/* ---------- STRENGTH ---------- */}
            <Card className="bg-white rounded-3xl shadow-soft p-5 md:p-6">
              <SectionHead title={t.strengthTitle} />
              <p className="text-sm text-stone font-inter mb-4">{t.strengthWhy}</p>

              {strength.length === 0 ? (
                <Empty>{t.strengthEmpty}</Empty>
              ) : (
                <>
                  <select
                    value={exIdx} onChange={(e) => setExIdx(Number(e.target.value))} aria-label={t.exercise}
                    className="h-11 rounded-xl border border-border bg-white px-3 text-sm font-inter text-charcoal mb-3 max-w-full"
                    data-testid="ex-select"
                  >
                    {strength.map((e, i) => <option key={e.exercise_id || i} value={i}>{e.name}</option>)}
                  </select>

                  <AxisUnit>{t.kg}</AxisUnit>
                  <div className="h-56 -ml-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={rmData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                        <CartesianGrid stroke={C_GRID} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="x" tick={{ fontSize: 11, fill: C_STONE }} tickLine={false} axisLine={{ stroke: C_GRID }} />
                        <YAxis
                          tick={{ fontSize: 11, fill: C_STONE }} tickLine={false} axisLine={false}
                          domain={rmScale.domain} ticks={rmScale.ticks} width={42}
                          tickFormatter={(v) => fmt(v, rmScale.step < 1 ? 1 : 0)}
                        />
                        <Tooltip content={
                          <ChartTip unit="kg" lang={language} extra={(p) => (
                            <p className="text-stone">{p.reps} {t.reps} × {fmt(p.load, 1)} {t.kg}{p.is_deload ? ` · ${t.deload}` : ''}</p>
                          )} />
                        } />
                        <Line
                          type="monotone" dataKey="one_rm" stroke={C_MAIN} strokeWidth={2.5}
                          isAnimationActive={false} activeDot={{ r: 5 }}
                          dot={(props) => {
                            const { cx, cy, payload, index } = props;
                            // Deload sessions get a hollow dot: a dip there is planned, not a regression.
                            return payload.is_deload
                              ? <circle key={index} cx={cx} cy={cy} r={4} fill="#fff" stroke={C_ALT} strokeWidth={2} strokeDasharray="2 1.5" />
                              : <circle key={index} cx={cx} cy={cy} r={3} fill={C_MAIN} />;
                          }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-stone font-inter mt-2">
                    <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: C_MAIN }} /> {t.estRM}</span>
                    <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full inline-block bg-white" style={{ border: `2px dashed ${C_ALT}` }} /> {t.deloadWeek}</span>
                  </div>

                  <h3 className="font-manrope font-bold text-sm text-charcoal mt-6 mb-2">{t.records}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm font-inter border-collapse min-w-[480px]">
                      <thead>
                        <tr className="text-stone text-xs uppercase tracking-wide">
                          <th className="text-left font-medium py-2 border-b border-border">{t.exCol}</th>
                          <th className="text-right font-medium py-2 pl-3 border-b border-border whitespace-nowrap">{t.bestSet}</th>
                          <th className="text-right font-medium py-2 pl-3 border-b border-border whitespace-nowrap">{t.rmCol}</th>
                          <th className="text-left font-medium py-2 pl-3 border-b border-border">{t.whenCol}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {strength.map((e, i) => (
                          <tr key={e.exercise_id || i} className="border-b border-border/60">
                            <td className="py-2 text-charcoal">
                              {e.name}
                              {e.bodyweight && <span className="text-stone text-xs"> · {t.bwTag}</span>}
                            </td>
                            {/* Show the LOAD, not the logged weight: on a bodyweight move the logged
                                value is the ADDED weight, so "10 × 0 kg" is what the user would read. */}
                            <td className="py-2 pl-3 text-right text-stone tabular-nums whitespace-nowrap">{e.pr.reps} × {fmt(e.pr.load, 1)} {t.kg}</td>
                            <td className="py-2 pl-3 text-right font-bold text-charcoal tabular-nums whitespace-nowrap">{fmt(e.pr.one_rm, 1)} {t.kg}</td>
                            <td className="py-2 pl-3 text-stone whitespace-nowrap">{shortDate(e.pr.date, language)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 space-y-2">
                    <p className="flex gap-2 text-xs text-stone font-inter">
                      <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: C_SAGE }} />
                      <span>{t.unilateralNote}</span>
                    </p>
                    {strength.some(e => e.bodyweight) && (
                      <p className="flex gap-2 text-xs text-stone font-inter">
                        <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: C_SAGE }} />
                        <span>{t.bodyweightNote}</span>
                      </p>
                    )}
                  </div>
                </>
              )}
            </Card>

            {/* ---------- VOLUME ---------- */}
            <Card className="bg-white rounded-3xl shadow-soft p-5 md:p-6">
              <SectionHead title={t.volumeTitle} />
              <p className="text-sm text-stone font-inter mb-4">{t.volumeWhy}</p>
              {volData.length === 0 || volData.every(v => !v.volume_kg) ? (
                <Empty>{t.volumeEmpty}</Empty>
              ) : (
                <>
                <AxisUnit>{volScale.step >= 1000 ? t.t : t.kg}</AxisUnit>
                <div className="h-56 -ml-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={volData} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                      <CartesianGrid stroke={C_GRID} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="x" tick={{ fontSize: 11, fill: C_STONE }} tickLine={false} axisLine={{ stroke: C_GRID }} />
                      <YAxis
                        tick={{ fontSize: 11, fill: C_STONE }} tickLine={false} axisLine={false}
                        domain={volScale.domain} ticks={volScale.ticks} width={42}
                        // Decimals derived from the step: rounding a 1.500 kg tick to "2t" would
                        // put a label on the axis that is simply not where the gridline is.
                        tickFormatter={(v) => (volScale.step >= 1000
                          ? fmt(v / 1000, volScale.step >= 1000 ? 0 : 1)
                          : fmt(v, 0))}
                      />
                      <Tooltip content={
                        <ChartTip unit={t.kg} lang={language} extra={(p) => (
                          <p className="text-stone">
                            {p.sessions_done}/{p.sessions_total} {t.sessionsShort}{p.is_deload ? ` · ${t.deload}` : ''}
                          </p>
                        )} />
                      } cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                      <Bar dataKey="volume_kg" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                        {volData.map((v, i) => (
                          <Cell key={i} fill={v.is_deload ? C_ALT : C_MAIN} fillOpacity={v.is_deload ? 0.75 : 1} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                </>
              )}
            </Card>

            {/* ---------- CARDIO ---------- */}
            <Card className="bg-white rounded-3xl shadow-soft p-5 md:p-6">
              <SectionHead title={t.cardioTitle} />
              <p className="text-sm text-stone font-inter mb-4">{t.cardioWhy}</p>
              {cardioTypes.length === 0 ? (
                <Empty>{t.cardioEmpty}</Empty>
              ) : (
                <div className="grid md:grid-cols-2 gap-5">
                  {cardioTypes.map(type => {
                    const rows = (cardio[type] || []).map(r => ({ ...r, x: shortDate(r.date, language) }));
                    const cScale = niceScale(rows.map(r => r.distance_m));
                    const swim = /nat|swim/.test(type);
                    // Only draw the pace chart once there's pace to draw, so a plan with no logged
                    // times doesn't show an empty second panel.
                    const paceRows = rows.filter(r => r.pace_sec != null);
                    const pScale = niceScale(paceRows.map(r => r.pace_sec));
                    const paceUnit = swim ? t.paceSwim : t.paceRun;
                    return (
                      <div key={type}>
                        <p className="font-manrope font-bold text-sm text-charcoal">{t.types[type] || type}</p>
                        <p className="text-xs text-stone font-inter mb-2">{t.metersPerSession}</p>
                        <div className="h-44 -ml-2">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                              <CartesianGrid stroke={C_GRID} strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="x" tick={{ fontSize: 10, fill: C_STONE }} tickLine={false} axisLine={{ stroke: C_GRID }} />
                              <YAxis
                                tick={{ fontSize: 10, fill: C_STONE }} tickLine={false} axisLine={false}
                                width={46} domain={cScale.domain} ticks={cScale.ticks}
                                tickFormatter={(v) => fmt(v, 0)}
                              />
                              <Tooltip content={<ChartTip unit="m" lang={language} />} />
                              <Line
                                type="monotone" dataKey="distance_m" stroke={C_MAIN} strokeWidth={2.5}
                                dot={{ r: 3, fill: C_MAIN }} activeDot={{ r: 5 }} isAnimationActive={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>

                        {paceRows.length > 0 && (
                          <>
                            <p className="text-xs text-stone font-inter mt-3 mb-2">{t.pacePerSession} ({paceUnit})</p>
                            <div className="h-40 -ml-2">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={paceRows.map(r => ({ ...r, x: shortDate(r.date, language) }))} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
                                  <CartesianGrid stroke={C_GRID} strokeDasharray="3 3" vertical={false} />
                                  <XAxis dataKey="x" tick={{ fontSize: 10, fill: C_STONE }} tickLine={false} axisLine={{ stroke: C_GRID }} />
                                  <YAxis
                                    tick={{ fontSize: 10, fill: C_STONE }} tickLine={false} axisLine={false}
                                    width={46} domain={pScale.domain} ticks={pScale.ticks}
                                    tickFormatter={fmtPace}
                                  />
                                  <Tooltip content={<ChartTip unit={paceUnit} lang={language} isPace />} />
                                  <Line
                                    type="monotone" dataKey="pace_sec" stroke={C_ALT} strokeWidth={2.5}
                                    dot={{ r: 3, fill: C_ALT }} activeDot={{ r: 5 }} isAnimationActive={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
}
